import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import LanguageToggle from './LanguageToggle';
import WGPlayerAd from './WGPlayerAd';
import { useLanguage } from '@lib/i18n';

/**
 * Props for Layout component
 */
interface LayoutProps {
  /** Page content to wrap with layout */
  children: React.ReactNode;
}

/**
 * Layout Component
 *
 * Main application layout wrapper providing:
 * - Responsive navigation header with logo, search, and menu
 * - Theme toggle (dark/light mode)
 * - Language selector
 * - Footer with links (privacy, terms, DMCA)
 *
 * Handles search form submission and navigation.
 * Uses Bootstrap responsive utilities for mobile/desktop layouts.
 *
 * @component
 */
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { t } = useLanguage();

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : '/search');
  };

  return (
    <div className="app-shell">
      <WGPlayerAd />
      <header>
        <nav className="navbar navbar-expand-lg bg-body py-3">
          <div className="container">
            <Link className="navbar-brand d-flex align-items-center" to="/">
              <img src="/assets/img/logo-block-blast.webp" alt="Block Blast - Play Free Online Games" width="102" height="52" className="me-2" />
            </Link>
            <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navcol-2">
              <span className="visually-hidden">Toggle navigation</span>
              <span className="navbar-toggler-icon"></span>
            </button>
            <div className="collapse navbar-collapse align-items-lg-center" id="navcol-2">
              <div className="search-container nav-search mt-3 mt-lg-0 ms-lg-auto me-lg-3 w-100 w-lg-auto">
                <form className="search-box d-flex align-items-center mb-0" onSubmit={onSubmit}>
                  <input
                    type="text"
                    className="form-control search-input"
                    id="search-bar"
                    name="search"
                    placeholder={t('common.search')}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <button className="btn btn-primary ms-2 search-btn" id="search-button" type="submit" aria-label="Search games">
                    <svg className="bi bi-search" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
                      <title>Search</title>
                      <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0"></path>
                    </svg>
                  </button>
                </form>
              </div>
              <ul className="navbar-nav align-items-lg-center">
                <li className="nav-item"><Link className="nav-link" to="/category/featured">{t('nav.featured')}</Link></li>
                <li className="nav-item"><Link className="nav-link" to="/category/latest">{t('nav.latest')}</Link></li>
                <li className="nav-item"><Link className="nav-link" to="/category/puzzle">Puzzle</Link></li>
                <li className="nav-item"><Link className="nav-link" to="/category/trending">{t('nav.trending')}</Link></li>
              </ul>
              <div className="ms-lg-3 d-flex gap-2 align-items-center">
                <LanguageToggle />
                <ThemeToggle />
              </div>
            </div>
          </div>
        </nav>
      </header>

      <main className="app-main container-fluid p-0">{children}</main>

      <footer>
        <div className="container">
          <div className="footer-content">
            <p className="copyright">{t('footer.copyright')}</p>
            <ul className="footer-link">
              <li><Link to="/privacy">{t('common.privacy')}</Link></li>
              <li><Link to="/terms">{t('common.terms')}</Link></li>
              <li><Link to="/dmca">{t('common.dmca')}</Link></li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
